import React, { useEffect, useMemo, useRef, useState } from "react";
import { generateInventoryPdf } from "../generateInventoryPdf";

/**
 * Calculates human-readable period options (e.g. "This week (15–21 Oct)") based on reference date.
 */
export function getPeriodOptions(referenceDate) {
  const ref = referenceDate ? new Date(referenceDate) : new Date();
  const now = Number.isNaN(ref.getTime()) ? new Date() : ref;

  // Day of week: 0 = Sun, 1 = Mon ...
  const day = now.getDay();
  // Monday of this week:
  const diffToMon = (day + 6) % 7;
  const thisMon = new Date(now);
  thisMon.setDate(now.getDate() - diffToMon);
  const thisSun = new Date(thisMon);
  thisSun.setDate(thisMon.getDate() + 6);

  // Last week:
  const lastMon = new Date(thisMon);
  lastMon.setDate(thisMon.getDate() - 7);
  const lastSun = new Date(lastMon);
  lastSun.setDate(lastMon.getDate() + 6);

  // Last 14 days:
  const l14 = new Date(now);
  l14.setDate(now.getDate() - 14);

  // Last 30 days:
  const l30 = new Date(now);
  l30.setDate(now.getDate() - 30);

  const fmtRange = (d1, d2) => {
    const m1 = d1.toLocaleString("en-US", { month: "short" });
    const m2 = d2.toLocaleString("en-US", { month: "short" });
    if (m1 === m2) {
      return `${d1.getDate()}–${d2.getDate()} ${m1}`;
    }
    return `${d1.getDate()} ${m1} – ${d2.getDate()} ${m2}`;
  };

  const fmtMonth = (dt) => dt.toLocaleString("en-US", { month: "short", year: "numeric" });

  return [
    { key: "this_week", label: `This week (${fmtRange(thisMon, thisSun)})`, text: `This week (${fmtRange(thisMon, thisSun)})`, start: thisMon, end: thisSun },
    { key: "last_week", label: `Last week (${fmtRange(lastMon, lastSun)})`, text: `Last week (${fmtRange(lastMon, lastSun)})`, start: lastMon, end: lastSun },
    { key: "last_14", label: `Last 14 days (${fmtRange(l14, now)})`, text: `Last 14 days (${fmtRange(l14, now)})`, start: l14, end: now },
    { key: "last_30", label: `Last 30 days (${fmtRange(l30, now)})`, text: `Last 30 days (${fmtRange(l30, now)})`, start: l30, end: now },
    { key: "this_month", label: `This month (${fmtMonth(now)})`, text: `This month (${fmtMonth(now)})`, start: new Date(now.getFullYear(), now.getMonth(), 1), end: now },
    { key: "custom", label: "Custom date range…", text: "Custom date range", isCustom: true },
  ];
}

export default function ReportDownloadModal({ isOpen, onClose, data, currency }) {
  const storeName = (data && data.store && data.store.name) || "Acme Home – US";
  const asOf = data && (data.asOf || (data.snapshot && data.snapshot.reportsFetchedAt));

  const periodOptions = useMemo(() => getPeriodOptions(asOf), [asOf]);

  const [selectedSeller, setSelectedSeller] = useState(storeName);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState("this_week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [sections, setSections] = useState({
    actToday: true, // always included
    dueThisWeek: true,
    inventoryHealth: true,
    inbound: true,
    moneyRecovery: true,
    doneForYou: true,
  });

  const [delivery, setDelivery] = useState("download"); // "download" | "email"
  const [sendWeekly, setSendWeekly] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (storeName) setSelectedSeller(storeName);
  }, [storeName]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    if (closeBtnRef.current) closeBtnRef.current.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const toggleSection = (key) => {
    if (key === "actToday") return; // always included
    setSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const currentPeriodObj = periodOptions.find((p) => p.key === selectedPeriodKey) || periodOptions[0];
  const periodText = currentPeriodObj.isCustom && customStartDate && customEndDate
    ? `Custom range (${customStartDate} to ${customEndDate})`
    : currentPeriodObj.text;

  const handleGenerate = async () => {
    setGenerating(true);
    setSuccessMsg("");
    try {
      if (delivery === "email") {
        // If email is chosen, still generate/prepare and notify user
        await generateInventoryPdf(data, {
          sellerName: selectedSeller,
          periodText,
          sections,
          currency,
        });
        setSuccessMsg("PDF generated and attached to seller ticket history!");
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        await generateInventoryPdf(data, {
          sellerName: selectedSeller,
          periodText,
          sections,
          currency,
        });
        onClose();
      }
    } catch (err) {
      console.error("Failed to generate seller report:", err);
      alert("Error generating PDF: " + (err.message || "Unknown error"));
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ia-seller-modal-scrim" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="ia-seller-report-title">
      <div className="ia-seller-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ia-seller-modal__head">
          <h2 id="ia-seller-report-title" className="ia-seller-modal__title">
            Seller report
          </h2>
          <button
            type="button"
            className="ia-seller-modal__close"
            onClick={onClose}
            aria-label="Close"
            ref={closeBtnRef}
          >
            &times;
          </button>
        </div>

        <p className="ia-seller-modal__desc">
          A PDF to send to the seller: red actions first, then this week, inventory, inbound and money recovery. Internal items such as System health are never included.
        </p>

        {/* Body */}
        <div className="ia-seller-modal__body">
          {/* Seller and Period row */}
          <div className="ia-seller-modal__row">
            <div className="ia-seller-modal__field">
              <label htmlFor="ia-seller-select" className="ia-seller-modal__label">
                Seller
              </label>
              <select
                id="ia-seller-select"
                className="ia-seller-modal__select"
                value={selectedSeller}
                onChange={(e) => setSelectedSeller(e.target.value)}
              >
                <option value={storeName}>{storeName}</option>
              </select>
            </div>

            <div className="ia-seller-modal__field">
              <label htmlFor="ia-period-select" className="ia-seller-modal__label">
                Period
              </label>
              <select
                id="ia-period-select"
                className="ia-seller-modal__select"
                value={selectedPeriodKey}
                onChange={(e) => setSelectedPeriodKey(e.target.value)}
              >
                {periodOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Custom Date Inputs (shown only if custom range selected) */}
          {currentPeriodObj.isCustom && (
            <div className="ia-seller-modal__custom-dates">
              <div className="ia-seller-modal__field">
                <label className="ia-seller-modal__label">Start Date</label>
                <input
                  type="date"
                  className="ia-seller-modal__input"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div className="ia-seller-modal__field">
                <label className="ia-seller-modal__label">End Date</label>
                <input
                  type="date"
                  className="ia-seller-modal__input"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Sections List */}
          <div className="ia-seller-modal__group">
            <div className="ia-seller-modal__group-title">Sections</div>
            <div className="ia-seller-modal__checklist">
              <label className="ia-seller-modal__check-label">
                <input
                  type="checkbox"
                  checked={sections.actToday}
                  disabled
                  readOnly
                  className="ia-seller-modal__checkbox"
                />
                <span>
                  Act today <span className="ia-seller-modal__muted">(always included)</span>
                </span>
              </label>

              <label className="ia-seller-modal__check-label">
                <input
                  type="checkbox"
                  checked={sections.dueThisWeek}
                  onChange={() => toggleSection("dueThisWeek")}
                  className="ia-seller-modal__checkbox"
                />
                <span>Due this week</span>
              </label>

              <label className="ia-seller-modal__check-label">
                <input
                  type="checkbox"
                  checked={sections.inventoryHealth}
                  onChange={() => toggleSection("inventoryHealth")}
                  className="ia-seller-modal__checkbox"
                />
                <span>Inventory health</span>
              </label>

              <label className="ia-seller-modal__check-label">
                <input
                  type="checkbox"
                  checked={sections.inbound}
                  onChange={() => toggleSection("inbound")}
                  className="ia-seller-modal__checkbox"
                />
                <span>Inbound shipments</span>
              </label>

              <label className="ia-seller-modal__check-label">
                <input
                  type="checkbox"
                  checked={sections.moneyRecovery}
                  onChange={() => toggleSection("moneyRecovery")}
                  className="ia-seller-modal__checkbox"
                />
                <span>Money recovery</span>
              </label>

              <label className="ia-seller-modal__check-label">
                <input
                  type="checkbox"
                  checked={sections.doneForYou}
                  onChange={() => toggleSection("doneForYou")}
                  className="ia-seller-modal__checkbox"
                />
                <span>Done for you and what we need from you</span>
              </label>
            </div>
          </div>

          {/* Delivery Options */}
          <div className="ia-seller-modal__group">
            <div className="ia-seller-modal__group-title">Delivery</div>
            <div className="ia-seller-modal__radiolist">
              <label className="ia-seller-modal__radio-label">
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="download"
                  checked={delivery === "download"}
                  onChange={() => setDelivery("download")}
                  className="ia-seller-modal__radio"
                />
                <span>Download PDF</span>
              </label>

              {/* <label className="ia-seller-modal__radio-label">
                <input
                  type="radio"
                  name="deliveryMethod"
                  value="email"
                  checked={delivery === "email"}
                  onChange={() => setDelivery("email")}
                  className="ia-seller-modal__radio"
                />
                <span>Email PDF to the seller's contact and attach to their ticket history</span>
              </label> */}

              {/* <label className="ia-seller-modal__check-label" style={{ marginTop: "6px" }}>
                <input
                  type="checkbox"
                  checked={sendWeekly}
                  onChange={(e) => setSendWeekly(e.target.checked)}
                  className="ia-seller-modal__checkbox"
                />
                <span>Send every Wednesday at 09:00 automatically</span>
              </label> */}
            </div>
          </div>

          {successMsg && (
            <div className="ia-seller-modal__alert ia-seller-modal__alert--success">
              {successMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ia-seller-modal__footer">
          <button
            type="button"
            className="ia-seller-modal__btn ia-seller-modal__btn--cancel"
            onClick={onClose}
            disabled={generating}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-icon btn-icon-left btn-background-color btn btn-secondary ia-seller-modal__btn ia-seller-modal__btn--primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? "Generating PDF…" : "Generate PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
